import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Badge } from '@/components/atoms/Badge';
import {
  RECOMMENDED_WEEKLY_ROUTINE,
  WEEKLY_ROUTINE_OPTIONS,
  type WeeklyRoutineOption,
  type WeeklyRoutineSelectorHandle,
  type WeeklyRoutineSelectorProps,
} from './WeeklyRoutineSelector.types';
import styles from './WeeklyRoutineSelector.module.scss';

/**
 * Selector de la meta semanal (1/3/5 historias) basado en radio buttons.
 * Se usa en el slide de personalización para que el alumno establezca su
 * rutina semanal — el valor se guarda como `weeklyGoal.targetStories`.
 *
 * Mismo patrón visual (cards) que [[level-selector]]: misma anatomía y
 * tokens. Mantiene la selección internamente y la expone vía ref
 * imperativa para que el padre la persista cuando le toque.
 */
export const WeeklyRoutineSelector = forwardRef<
  WeeklyRoutineSelectorHandle,
  WeeklyRoutineSelectorProps
>(function WeeklyRoutineSelector(
  { defaultValue, disabled = false, onValidityChange },
  ref
) {
  const { t } = useTranslation();
  // La opción recomendada arranca preseleccionada para que el alumno
  // pueda avanzar sin tocar nada si la acepta. Si el padre pasa un
  // `defaultValue` (p. ej. la meta semanal actual), gana ese.
  const [selected, setSelected] = useState<WeeklyRoutineOption | null>(
    defaultValue ?? RECOMMENDED_WEEKLY_ROUTINE
  );

  const lastNotifiedValidRef = useRef<boolean | null>(null);
  useEffect(() => {
    const isValid = selected !== null;
    if (lastNotifiedValidRef.current === isValid) return;
    lastNotifiedValidRef.current = isValid;
    onValidityChange?.(isValid);
  }, [selected, onValidityChange]);

  useImperativeHandle(
    ref,
    () => ({
      getSelected: () => selected,
      isValid: () => selected !== null,
    }),
    [selected]
  );

  return (
    <div className={styles.selector}>
      <RadioGroupPrimitive.Root
        className={styles.selector__list}
        value={selected != null ? String(selected) : undefined}
        onValueChange={(v) =>
          setSelected(Number(v) as WeeklyRoutineOption)
        }
        disabled={disabled}
        aria-label={t('weeklyRoutineSelector.ariaLabel')}
      >
        {WEEKLY_ROUTINE_OPTIONS.map((count) => {
          const id = `weekly-routine-${count}`;
          const isRecommended = count === RECOMMENDED_WEEKLY_ROUTINE;
          return (
            <label key={count} htmlFor={id} className={styles.selector__card}>
              <RadioGroupPrimitive.Item
                value={String(count)}
                id={id}
                className={styles.selector__radio}
                data-no-press-scale=""
              >
                <RadioGroupPrimitive.Indicator
                  className={styles.selector__indicator}
                />
              </RadioGroupPrimitive.Item>
              <span className={styles.selector__label}>
                {t('weeklyRoutineSelector.option', { count })}
              </span>
              {isRecommended && (
                <Badge
                  variant="secondary"
                  size="sm"
                  className={styles.selector__badge}
                >
                  {t('weeklyRoutineSelector.recommended')}
                </Badge>
              )}
            </label>
          );
        })}
      </RadioGroupPrimitive.Root>
      <p className={styles.helper}>{t('weeklyRoutineSelector.helper')}</p>
    </div>
  );
});

WeeklyRoutineSelector.displayName = 'WeeklyRoutineSelector';
